import React, { useMemo, useState, Fragment } from 'react';
import { RowData, Inputs, PackageType, BuildPayType } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  // Track which ad spend cell is being edited
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  // Track which campaign cell is being edited
  const [editingCampaign, setEditingCampaign] = useState<number | null>(null);
  const [editCampaignValue, setEditCampaignValue] = useState<string>('');
  // Track which fee cells are being edited
  const [editingBuildFee, setEditingBuildFee] = useState<number | null>(null);
  const [editBuildFeeValue, setEditBuildFeeValue] = useState<string>('');
  const [editingPlatformFee, setEditingPlatformFee] = useState<number | null>(null);
  const [editPlatformFeeValue, setEditPlatformFeeValue] = useState<string>('');
  const [editingCampaignFee, setEditingCampaignFee] = useState<number | null>(null);
  const [editCampaignFeeValue, setEditCampaignFeeValue] = useState<string>('');
  // Timeframe state
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  // Revenue shift state
  const [revenueShift, setRevenueShift] = useState<number>(0);
  // Sales delay state (number of months to zero out at the start)
  const [salesDelay, setSalesDelay] = useState<number>(0);
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    recruiting: false,
    activeReps: false,
    investment: false,
    revenue: false // Collapse Revenue & Profit by default too
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  // Handle monthly campaign overrides
  const handleCampaignChange = (monthIndex: number, value: number) => {
    // monthIndex 0 is Build, 1 is Month 1, etc.
    // But our array is 12 months (indices 0-11 for Month 1-12)
    // So if monthIndex is 0 (Build), we ignore or handle appropriately
    if (monthIndex === 0) return; // Cannot change build month campaigns
    const arrayIndex = monthIndex - 1;
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    // Allow zero campaigns to illustrate pausing hiring
    newMonthlyCampaigns[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns
    });
  };
  // Handle pushing campaign value to all months to the right
  const handlePushRight = (monthIndex: number) => {
    if (monthIndex === 0) return; // Cannot push from Build month
    const arrayIndex = monthIndex - 1;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    // Create new array with the value propagated to all months to the right
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns
    });
  };
  // Handle monthly ad spend changes
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return; // Cannot change build month ad spend
    const arrayIndex = monthIndex - 1;
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyAdSpend[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  // Handle pushing ad spend value to all months to the right
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return; // Cannot push from Build month
    const arrayIndex = monthIndex - 1;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const valueToPropagate = currentMonthlyAdSpend[arrayIndex];
    // Create new array with the value propagated to all months to the right
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  // Start editing ad spend
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return; // Cannot edit Build month
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(currentValue.toString());
  };
  // Save ad spend edit
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue) || 0;
      handleAdSpendChange(editingAdSpend, value);
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  // Cancel ad spend edit
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  // Handle Enter key to save
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  // Start editing campaign
  const startEditingCampaign = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return; // Cannot edit Build month
    setEditingCampaign(monthIndex);
    setEditCampaignValue(currentValue.toString());
  };
  // Save campaign edit
  const saveCampaignEdit = () => {
    if (editingCampaign !== null) {
      const value = parseFloat(editCampaignValue) || 0;
      handleCampaignChange(editingCampaign, value);
      setEditingCampaign(null);
      setEditCampaignValue('');
    }
  };
  // Cancel campaign edit
  const cancelCampaignEdit = () => {
    setEditingCampaign(null);
    setEditCampaignValue('');
  };
  // Handle Enter key to save campaign
  const handleCampaignKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignEdit();
    }
  };
  // Handle build fee editing
  const startEditingBuildFee = (monthIndex: number, currentValue: number) => {
    setEditingBuildFee(monthIndex);
    setEditBuildFeeValue(currentValue.toString());
  };
  const saveBuildFeeEdit = () => {
    if (editingBuildFee !== null) {
      // Store custom build fee override
      // For now, just close the editor - we'd need to add state management for custom fees
      setEditingBuildFee(null);
      setEditBuildFeeValue('');
    }
  };
  const cancelBuildFeeEdit = () => {
    setEditingBuildFee(null);
    setEditBuildFeeValue('');
  };
  const handleBuildFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveBuildFeeEdit();
    } else if (e.key === 'Escape') {
      cancelBuildFeeEdit();
    }
  };
  // Handle platform fee editing
  const startEditingPlatformFee = (monthIndex: number, currentValue: number) => {
    setEditingPlatformFee(monthIndex);
    setEditPlatformFeeValue(currentValue.toString());
  };
  const savePlatformFeeEdit = () => {
    if (editingPlatformFee !== null) {
      setEditingPlatformFee(null);
      setEditPlatformFeeValue('');
    }
  };
  const cancelPlatformFeeEdit = () => {
    setEditingPlatformFee(null);
    setEditPlatformFeeValue('');
  };
  const handlePlatformFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      savePlatformFeeEdit();
    } else if (e.key === 'Escape') {
      cancelPlatformFeeEdit();
    }
  };
  // Handle campaign fee editing
  const startEditingCampaignFee = (monthIndex: number, currentValue: number) => {
    setEditingCampaignFee(monthIndex);
    setEditCampaignFeeValue(currentValue.toString());
  };
  const saveCampaignFeeEdit = () => {
    if (editingCampaignFee !== null) {
      setEditingCampaignFee(null);
      setEditCampaignFeeValue('');
    }
  };
  const cancelCampaignFeeEdit = () => {
    setEditingCampaignFee(null);
    setEditCampaignFeeValue('');
  };
  const handleCampaignFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignFeeEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignFeeEdit();
    }
  };
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition && revenueShift === 0 && salesDelay === 0) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply sales delay (zero out sales for first N months)
      if (salesDelay > 0 && idx > 0 && idx <= salesDelay) {
        // Zero out sales for months 1 through salesDelay
        newRow.totalSales = 0;
        newRow.grossRevenue = 0;
      }
      // Apply revenue shift (this can further modify grossRevenue)
      if (revenueShift !== 0) {
        // Calculate source index (e.g. shift +1 means Month 2 takes from Month 1)
        const sourceIdx = idx - revenueShift;
        // Only shift if source is valid and we're not in Build month (unless source is also Build)
        // We generally don't want to shift revenue INTO the build month (idx 0)
        if (idx > 0 && sourceIdx >= 0 && sourceIdx < rows.length) {
          // Only apply revenue shift if sales weren't already zeroed out
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = rows[sourceIdx].totalSales;
          }
          newRow.grossRevenue = rows[sourceIdx].grossRevenue;
        } else if (idx > 0) {
          // If out of bounds (e.g. shifting right leaves gap at start), zero out
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = 0;
          }
          newRow.grossRevenue = 0;
        }
        // If idx === 0 (Build), keep original values (usually 0)
      }
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = newRow.totalSales * 0.5;
        // Note: grossRevenue might have been updated by shift above
        newRow.grossRevenue = newRow.grossRevenue * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        // Halve all active counts
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        // Recalculate sales based on reduced active reps
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        // Apply attrition multiplier to the current state of newRow
        newRow.totalSales = newRow.totalSales * retentionMultiplier;
        newRow.grossRevenue = newRow.grossRevenue * retentionMultiplier;
      }
      // Recalculate profits based on final revenue
      // Gross Profit = Revenue * Margin
      newRow.grossProfit = newRow.grossRevenue * (inputs.gpm / 100);
      // Recalculate net profit
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition, revenueShift, salesDelay, inputs.gpm, inputs.ticket]);
  // Recalculate cumulative profit for adjusted rows
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  // Filter rows based on timeframe
  const displayRows = useMemo(() => {
    // Always include Build (index 0) + the selected number of months
    return finalRows.slice(0, timeframe + 1);
  }, [finalRows, timeframe]);
  // Helper to get formula explanation for each metric with actual numbers
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex; // 0 = Launch, 1 = Month 1, etc.
    switch (metricKey) {
      case 'activeCampaigns':
        return `Number of active campaigns for this month.\nAdjusting this will recalculate ad spend and hires.`;
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ CPH = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 0) return 'Build period - no active reps yet';
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        return `Formula: Previous month's launched reps × Day 30 retention\n\nActual: Single cohort at 30-60 days = ${row.active30d.toFixed(1)} reps\n\nNote: This represents reps who are 30-60 days old. Should stay constant if hiring volume is consistent.`;
      case 'active60d':
        if (monthNum <= 2) return monthNum === 0 ? 'Build period' : 'Not enough time elapsed (need 60 days)';
        return `Formula: 2 months ago launched reps × Day 60 retention\n\nActual: Single cohort at 60-90 days = ${row.active60d.toFixed(1)} reps\n\nNote: This represents reps who are 60-90 days old. Should stay constant if hiring volume is consistent.`;
      case 'active90d':
        if (monthNum < 4) return monthNum === 0 ? 'Build period' : 'Building cohort (need 90 days)...';
        if (monthNum === 4) {
          return `Formula: First cohort reaching 90 days\n\nActual: ${row.active90d.toFixed(1)} reps (90-120 days old)\n\nNote: Month 4 is the first month with reps at 90+ days. No veterans yet.`;
        }
        return `Formula: Cumulative count of all reps 90+ days old\n\nActual: ${row.active90d.toFixed(1)} (90-120d cohort) + ${row.veterans.toFixed(1)} (120d+ veterans)\n= ${(row.active90d + row.veterans).toFixed(1)} total\n\nNote: Grows cumulatively as veterans accumulate with monthly attrition applied.`;
      case 'veterans':
        if (monthNum <= 4) return 'No veterans yet (need 120+ days)';
        return `Formula: Previous month's 90d+ reps with attrition + new 120d graduates\n\nActual: ${row.veterans.toFixed(1)} veterans (120+ days old)\n\nNote: Monthly attrition based on Day 90 to 1 Year retention rate.`;
      case 'teamSize':
        const ts_launched = row.launched * 0.5;
        const ts_30to60 = row.active30d;
        const ts_60to90 = row.active60d;
        const ts_90plus = rowIndex === 4 ? row.active90d : row.active90d + row.veterans;
        return `Formula: Launched (0-30d at 50%) + Active 30d+ + Active 60d+ + Active 90d+\n\nBreakdown:\n• Launched (0-30d): ${ts_launched.toFixed(1)} (50% weight)\n• Active 30d+ (30-60d): ${ts_30to60.toFixed(1)}\n• Active 60d+ (60-90d): ${ts_60to90.toFixed(1)}\n• Active 90d+ (90d+): ${ts_90plus.toFixed(1)}\n\nTotal: ${(ts_launched + ts_30to60 + ts_60to90 + ts_90plus).toFixed(1)}\n\nNote: Each segment represents distinct age groups - no double-counting.`;
      case 'adSpend':
        const scenarioNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario active but ad spend unchanged' : '';
        return `Formula: Monthly ad spend × campaigns\n\nActual: ${fmtMoney(row.adSpend)} total ad spend${scenarioNote}`;
      case 'buildFee':
        if (row.buildFee === 0) return 'No build fee this month';
        return `Formula: Build & Implementation Fee\n\nActual: ${fmtMoney(row.buildFee)} (${monthNum === 0 ? 'Build' : `Month ${monthNum} payment`})`;
      case 'platformFee':
        return `Formula: HIRE platform monthly fee\n\nActual: ${fmtMoney(row.platformFee)} (system access & support)`;
      case 'campaignFee':
        return `Formula: HIRE campaign management fee × campaigns\n\nActual: ${fmtMoney(row.campaignFee)} (campaign management)`;
      case 'placementBonus':
        const pkg = inputs.pkg;
        if (pkg === 'edge') {
          return 'HIRE Edge package does not include placement bonuses';
        }
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        // Billing for PREVIOUS month's launched reps
        const prevMonthForPlacement = finalRows[monthNum - 1];
        const bonusPerLaunched = 250;
        const rawPlacementBonus = prevMonthForPlacement.launched * bonusPerLaunched;
        const roundedPlacementBonus = Math.floor(rawPlacementBonus / 250) * 250;
        const placementAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Launched reps reduced by 50%' : '';
        // Build cohort breakdown
        let placementBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        placementBreakdown += `Billing for reps launched in ${prevMonthForPlacement.period}:\n`;
        placementBreakdown += `• ${prevMonthForPlacement.launched.toFixed(1)} reps launched\n`;
        placementBreakdown += `• Billed the following month (this month)\n`;
        placementBreakdown += `• $250 per launched rep`;
        return `Formula: Previous month's launched reps × $250\n(Billed month after launch)\n\nActual: ${prevMonthForPlacement.launched.toFixed(1)} launched × $250 = ${fmtMoney(rawPlacementBonus)}\nRounded down: ${fmtMoney(roundedPlacementBonus)}${placementBreakdown}${placementAttritionNote}`;
      case 'retentionBonus':
        const pkgType = inputs.pkg;
        if (pkgType === 'edge') {
          return 'HIRE Edge package does not include retention bonuses';
        }
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        // Billing for PREVIOUS month's active reps at each milestone
        const prevMonthForRetention = finalRows[monthNum - 1];
        let bonus30d = 0;
        let bonus60d = 0;
        let bonus90d = 0;
        let explanation = "Formula: Previous month's active reps × $250\n";
        explanation += '(Billed month after hitting milestone)\n';
        explanation += 'Rounded down to nearest $250\n\n';
        if (pkgType === 'advantage' || pkgType === 'summit') {
          const raw30d = prevMonthForRetention.active30d * 250;
          bonus30d = Math.floor(raw30d / 250) * 250;
          explanation += `30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} active × $250 = ${fmtMoney(bonus30d)}\n`;
          const raw60d = prevMonthForRetention.active60d * 250;
          bonus60d = Math.floor(raw60d / 250) * 250;
          explanation += `60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} active × $250 = ${fmtMoney(bonus60d)}\n`;
          const raw90d = prevMonthForRetention.active90d * 250;
          bonus90d = Math.floor(raw90d / 250) * 250;
          explanation += `90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} active × $250 = ${fmtMoney(bonus90d)}\n`;
        }
        const totalBonus = bonus30d + bonus60d + bonus90d;
        explanation += `Total: ${fmtMoney(totalBonus)}`;
        // Build cohort breakdown
        let retentionBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        retentionBreakdown += `Billing for milestones hit in ${prevMonthForRetention.period}:\n`;
        if (prevMonthForRetention.active30d > 0) {
          // Active 30d+ are from cohort 1 month prior (now at 30-60 days)
          const cohortMonth = monthNum - 2;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active60d > 0) {
          // Active 60d+ are from cohort 2 months prior (now at 60-90 days)
          const cohortMonth = monthNum - 3;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active90d > 0) {
          // Active 90d+ are from cohort 3 months prior (now at 90-120 days)
          const cohortMonth = monthNum - 4;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        const retentionAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return explanation + retentionBreakdown + retentionAttritionNote;
      case 'bonuses':
        return `Formula: Launch + Day 30 + Day 60 + Day 90 bonuses\n\nActual: ${fmtMoney(row.bonuses)} total bonuses this month`;
      case 'totalCost':
        return `Formula: Sum of all fees, bonuses, and ad spend (rounded)\n\nActual: ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario: Sales reduced by 50%' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return `Formula: Sales from reps in first 90 days + Veteran sales\n\nActual: Reps 0-90 days making ${inputs.salesFirst90Total} total sales over 90 days + ${row.veterans.toFixed(1)} veterans × ${inputs.salesVetMonthly} sales/month\n= ${row.totalSales.toFixed(1)} total sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Formula: Pullthrough / Realization Rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales are realized`;
      case 'grossRevenue':
        const revenueNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied to sales' : '';
        const residualNote = row.residualRevenue && row.residualRevenue > 0 ? `\n\nResidual Revenue: ${fmtMoney(row.residualRevenue)}\nInitial Sales Revenue: ${fmtMoney(row.grossRevenue - row.residualRevenue)}` : '';
        return `Formula: Total Sales × Realization Rate × Avg Ticket${residualNote}\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}${revenueNote}`;
      case 'grossProfit':
        const profitNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied' : '';
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}${profitNote}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Formula: Sum of all net profits from Build to ${row.period}\n\nActual: Running total = ${fmtMoney(row.cumProfit)}`;
      case 'roi':
        // Calculate cumulative values for tooltip
        let cumGrossProfit = 0;
        let cumTotalCost = 0;
        for (let i = 0; i <= rowIndex; i++) {
          cumGrossProfit += finalRows[i].grossProfit;
          cumTotalCost += finalRows[i].totalCost;
        }
        const roiPercent = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
        return `Formula: (Gross Profit To Date ÷ Total Investment To Date) × 100\n\nActual: ${fmtMoney(cumGrossProfit)} ÷ ${fmtMoney(cumTotalCost)} × 100\n= ${roiPercent.toFixed(1)}%\n\nNote: Shows return on investment as a percentage. Values over 100% indicate profitability.`;
      case 'higherPartnership':
        return `Subtotal of Build, Platform, Campaign, and Bonus fees`;
      default:
        return 'Calculation details';
    }
  };
  // Define metrics to display (rows in the transposed table)
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-blue-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'activeCampaigns',
      label: 'Active Campaigns',
      format: (v: number) => {
        // Format to show decimals only when needed (e.g., 1.5 shows, but 1.0 shows as 1)
        return v % 1 === 0 ? v.toString() : v.toFixed(1);
      },
      editable: true,
      clickToEdit: true,
      centered: true
    }, {
      key: 'adSpendPerCampaign',
      label: 'Ad Spend per Campaign',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true,
      centered: true
    }, {
      key: 'avgCPH',
      label: 'Average CPH',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString(),
      bold: true
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-purple-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Active Reps (Average)',
      format: (v: number) => v.toFixed(1),
      bold: true,
      calculated: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Monthly Investment',
    color: 'bg-red-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [...(inputs.pkg ? [{
      key: 'buildFee' as const,
      label: 'Build & Implementation',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }, {
      key: 'platformFee' as const,
      label: 'Platform Fee',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }, {
      key: 'campaignFee' as const,
      label: 'Campaign Fee',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }, {
      key: 'placementBonus' as const,
      label: 'Placement Bonus',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'retentionBonus' as const,
      label: 'Retention Bonus',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'higherPartnership' as const,
      label: 'Higher Partnership',
      format: (v: number) => fmtMoney(v),
      bold: true
    }] : []), {
      key: 'adSpend',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-green-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'totalSales',
      label: <div className="flex items-center justify-between w-full group" data-id="element-388">
              <span data-id="element-389">Total Sales</span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200" data-id="element-390">
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.max(0, prev - 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Reduce sales delay (enable sales in earlier months)" data-id="element-391">
                  ◀
                </button>
                {salesDelay > 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-392">
                    {salesDelay}mo
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.min(11, prev + 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Delay sales (zero out sales in early months)" data-id="element-393">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Realization Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, ...(inputs.revenueCadence !== 'one-time' ? [{
      key: 'residualRevenue' as const,
      label: inputs.revenueCadence === 'monthly' ? 'Monthly Residual Revenue' : 'Quarterly Residual Revenue',
      format: (v: number) => fmtMoney(v)
    }] : []), {
      key: 'grossRevenue',
      label: <div className="flex items-center justify-between w-full group" data-id="element-394">
              <span data-id="element-395">
                {inputs.revenueCadence !== 'one-time' ? 'Total Monthly Revenue' : 'Monthly Gross Revenue'}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200" data-id="element-396">
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev - 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue earlier" data-id="element-397">
                  ◀
                </button>
                {revenueShift !== 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-398">
                    {revenueShift > 0 ? '+' : ''}
                    {revenueShift}
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev + 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue later" data-id="element-399">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Monthly Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'netProfit',
      label: 'Monthly Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      bg: 'bg-blue-50'
    }, {
      key: 'roi',
      label: 'ROI',
      format: (v: number) => `${Math.round(v)}%`,
      highlight: true,
      bg: 'bg-purple-50'
    }]
  }];
  return <div className="bg-white border-2 border-gray-200 rounded p-4 md:p-6 shadow-lg" data-id="element-400">
      <div className="flex items-center justify-between mb-4" data-id="element-401">
        <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-402">
          {inputs.companyName ? `Monthly Projections for ${inputs.companyName}` : 'Monthly Projections'}
        </h2>

        <div className="flex items-center gap-3" data-id="element-403">
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-404">
            <button onClick={() => setTimeframe(3)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-405">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-406">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-407">
              12M
            </button>
          </div>

          {/* Split Toggle Button */}
          <button onClick={() => {
          // Toggle between upfront and split payment
          onInputsChange({
            ...inputs,
            buildPay: inputs.buildPay === 'upfront' ? 'two' : 'upfront'
          });
        }} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.buildPay === 'two' ? 'bg-amber text-navy border-amber' : 'bg-white text-gray-700 border-gray-300 hover:border-amber'}`} data-id="element-408">
            {inputs.buildPay === 'two' ? '✓ ' : ''}Split
          </button>

          {/* Package Dropdown */}
          <select value={inputs.pkg} onChange={e => onInputsChange({
          ...inputs,
          pkg: e.target.value as PackageType
        })} className="px-3 py-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-xs font-semibold" data-id="element-409">
            <option value="" data-id="element-410">Select</option>
            <option value="edge" data-id="element-411">HIRE Edge</option>
            <option value="advantage" data-id="element-412">HIRE Advantage</option>
            <option value="summit" data-id="element-413">HIRE Summit</option>
          </select>

          {/* Scenario Toggles */}
          <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-414">
            {halfSales ? '✓ ' : ''}50% Sales Scenario
          </button>
          <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-415">
            {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border-2 border-gray-200" data-id="element-416">
        <table className="w-full border-collapse text-sm" data-id="element-417">
          <thead className="sticky top-0 z-20" data-id="element-418">
            <tr className="bg-navy text-white" data-id="element-419">
              <th className="sticky left-0 z-30 bg-navy text-xs font-bold uppercase tracking-wide p-2 text-left min-w-[160px] border-r-2 border-amber/30" data-id="element-420">
                <span className="inline-block" data-id="element-421">Metric</span>
              </th>
              {displayRows.map((row, idx) => {
              // Abbreviate month names: "Build" stays, "Month 1" becomes "Mo 1"
              const displayPeriod = row.period === 'Build' ? 'Build' : row.period.replace('Month', 'Mo');
              // Add thicker border after every 3 months (quarterly separator)
              const isQuarterEnd = idx > 0 && idx % 3 === 0;
              return <th key={row.period} className={`bg-navy text-xs font-bold uppercase tracking-wide p-2 text-center whitespace-nowrap min-w-[80px] ${isQuarterEnd ? 'border-r-4 border-amber' : 'border-r border-amber/20'}`} data-id="element-422">
                    {displayPeriod}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="bg-white" data-id="element-423">
            {metricGroups.map((group, groupIdx) => {
            const isExpanded = group.collapsible ? expandedSections[group.id as keyof typeof expandedSections] : true;
            const showDetails = !group.collapsible || isExpanded;
            return <Fragment key={group.id} data-id="element-424">
                  {/* Group Header Row */}
                  {!group.noHeader && <tr className={`${group.color} border-b-2 border-gray-300`} data-id="element-425">
                      <td className={`sticky left-0 z-10 ${group.color} p-3 font-bold text-gray-900 min-w-[180px] text-sm border-r-2 border-gray-300 ${group.collapsible ? 'cursor-pointer hover:bg-opacity-80' : ''}`} onClick={() => group.collapsible && toggleSection(group.id as keyof typeof expandedSections)} data-id="element-426">
                        <div className="flex items-center gap-2" data-id="element-427">
                          {group.collapsible && <span className="text-navy" data-id="element-428">
                              {isExpanded ? '▼' : '▶'}
                            </span>}
                          <span className="uppercase tracking-wide text-sm inline-block" data-id="element-429">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                  const isEvenColumn = rowIdx % 2 === 0;
                  const zebraClass = ''; // Removed to follow the instruction; previously it was: const zebraClass = isEvenColumn ? '' : 'bg-gray-100/50'
                  const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                  return <td key={row.period} className={`${group.color} ${zebraClass} p-2 text-center font-bold text-gray-900 border-b-2 border-gray-300 ${isQuarterEnd ? 'border-r-4 border-amber' : 'border-r border-gray-200'}`} data-id="element-430">
                            {/* No data in headers */}
                          </td>;
                })}
                    </tr>}

                  {/* Detail Rows */}
                  {showDetails && group.metrics.map((metric, metricIdx) => {
                const isLastInGroup = metricIdx === group.metrics.length - 1;
                const borderClass = isLastInGroup ? 'border-b-2 border-gray-300' : 'border-b border-gray-200';
                const isEvenRow = metricIdx % 2 === 0;
                const rowBgClass = isEvenRow ? 'bg-white' : 'bg-gray-50/30';
                const bgClass = metric.bg || rowBgClass;
                // Reduced height from h-14 to h-11
                return <tr key={metric.key} className={`hover:bg-gray-100 h-11 ${group.noHeader ? group.color : bgClass}`} data-id="element-431">
                          <td className={`sticky left-0 z-10 ${group.noHeader ? group.color : bgClass} p-2 ${group.noHeader ? '' : 'pl-6'} text-gray-900 ${metric.bold ? 'font-bold text-xs' : 'font-medium text-xs'} ${borderClass} min-w-[160px] border-r-2 border-gray-300`} data-id="element-432">
                            <span className="inline-block" data-id="element-433">{metric.label}</span>
                          </td>
                          {displayRows.map((row, rowIdx) => {
                    let value: number;
                    let displayValue: React.ReactNode;
                    // Helper to calculate bonus for a specific row
                    const getPlacementBonus = (r: RowData, idx: number) => {
                      const pkg = inputs.pkg;
                      if (pkg === 'advantage' || pkg === 'summit') {
                        if (idx === 0) return 0;
                        const prevRow = finalRows[idx - 1];
                        const rawBonus = prevRow.launched * 250;
                        return Math.floor(rawBonus / 250) * 250;
                      }
                      return 0;
                    };
                    const getRetentionBonus = (r: RowData, idx: number) => {
                      const pkg = inputs.pkg;
                      if (pkg === 'advantage' || pkg === 'summit') {
                        if (idx === 0) return 0;
                        const prevRow = finalRows[idx - 1];
                        let bonus = 0;
                        const raw30d = prevRow.active30d * 250;
                        bonus += Math.floor(raw30d / 250) * 250;
                        const raw60d = prevRow.active60d * 250;
                        bonus += Math.floor(raw60d / 250) * 250;
                        const raw90d = prevRow.active90d * 250;
                        bonus += Math.floor(raw90d / 250) * 250;
                        return bonus;
                      }
                      return 0;
                    };
                    // Get ad spend per campaign for this month
                    const getAdSpendPerCampaign = (idx: number) => {
                      if (idx === 0) return 0; // Build month
                      const monthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
                      return monthlyAdSpend[idx - 1];
                    };
                    // Get average CPH for this month
                    const getAvgCPH = (idx: number) => {
                      if (idx === 0) return 0; // Build month
                      // Return the input CPH value instead of calculated
                      return inputs.cph;
                    };
                    if (metric.key === 'teamSize') {
                      // Team Size calculation: sum all active reps without double-counting
                      const launched0to30 = row.launched * 0.5;
                      const active30to60 = row.active30d; // Single cohort at 30-60 days
                      const active60to90 = row.active60d; // Single cohort at 60-90 days
                      const active90plus = row.active90d + row.veterans; // Cumulative 90+ days
                      value = launched0to30 + active30to60 + active60to90 + active90plus;
                    } else if (metric.key === 'active30d') {
                      // Active 30d+ = just the single cohort at 30-60 days (NOT cumulative)
                      value = row.active30d;
                    } else if (metric.key === 'active60d') {
                      // Active 60d+ = just the single cohort at 60-90 days (NOT cumulative)
                      value = row.active60d;
                    } else if (metric.key === 'active90d') {
                      // Active 90d+ = cumulative of all reps 90+ days old
                      // Month 4: Just the first cohort at 90 days (row.active90d)
                      // Month 5+: Previous 90d+ with attrition + new graduates
                      // For Month 4 (rowIdx = 4, first month with 90d reps)
                      if (rowIdx === 4) {
                        value = row.active90d; // Just the first cohort, no veterans yet
                      } else if (rowIdx > 4) {
                        // Month 5+: active90d (new graduates) + veterans (previous 90d+ with attrition)
                        value = row.active90d + row.veterans;
                      } else {
                        // Months 0-3: No 90d+ reps yet
                        value = 0;
                      }
                    } else if (metric.key === 'adSpendPerCampaign') {
                      value = getAdSpendPerCampaign(rowIdx);
                    } else if (metric.key === 'avgCPH') {
                      value = getAvgCPH(rowIdx);
                    } else if (metric.key === 'placementBonus') {
                      value = getPlacementBonus(row, rowIdx);
                    } else if (metric.key === 'retentionBonus') {
                      value = getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'higherPartnership') {
                      // Subtotal: Build + Platform + Campaign + Bonuses
                      const pBonus = getPlacementBonus(row, rowIdx);
                      const rBonus = getRetentionBonus(row, rowIdx);
                      value = row.buildFee + row.platformFee + row.campaignFee + pBonus + rBonus;
                    } else if (metric.key === 'totalCost') {
                      // Total Investment: Higher Partnership + Ad Spend
                      // Ensure we sum the rounded numbers
                      const pBonus = getPlacementBonus(row, rowIdx);
                      const rBonus = getRetentionBonus(row, rowIdx);
                      const subtotal = row.buildFee + row.platformFee + row.campaignFee + pBonus + rBonus;
                      value = subtotal + row.adSpend;
                    } else if (metric.key === 'roi') {
                      // ROI = (Cumulative Gross Profit / Cumulative Total Investment) * 100
                      // Calculate cumulative gross profit up to this month
                      let cumGrossProfit = 0;
                      let cumTotalCost = 0;
                      for (let i = 0; i <= rowIdx; i++) {
                        cumGrossProfit += finalRows[i].grossProfit;
                        cumTotalCost += finalRows[i].totalCost;
                      }
                      // ROI percentage
                      value = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
                    } else {
                      value = row[metric.key as keyof RowData] as number;
                    }
                    const formatted = metric.format(value);
                    const isEvenColumn = rowIdx % 2 === 0;
                    const columnShade = isEvenColumn ? 'bg-white' : 'bg-gray-100/30';
                    const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                    let cellClass = `p-1.5 text-center whitespace-nowrap ${borderClass} relative group min-w-[80px] text-xs ${group.noHeader ? group.color : `${bgClass} ${columnShade}`} ${isQuarterEnd ? 'border-r-4 border-amber' : isEvenColumn ? 'border-r-2 border-gray-300' : 'border-r border-gray-200'}`;
                    if (metric.highlight) {
                      if (value >= 0) {
                        cellClass += ' text-green-600 font-bold';
                      } else {
                        cellClass += ' text-red-600 font-bold';
                      }
                    } else if (metric.bold) {
                      cellClass += ' font-bold text-gray-900';
                    } else {
                      cellClass += ' text-gray-700';
                    }
                    // Handle editable active campaigns
                    if (metric.key === 'activeCampaigns' && metric.editable && rowIdx > 0) {
                      // Click-to-edit campaigns
                      if (editingCampaign === rowIdx) {
                        displayValue = <div className="flex justify-center items-center gap-0.5" data-id="element-434">
                                    <input type="number" min="0" step="0.5" value={editCampaignValue} onChange={e => setEditCampaignValue(e.target.value)} onKeyDown={handleCampaignKeyDown} onBlur={saveCampaignEdit} autoFocus className="w-16 text-center border-2 border-amber rounded p-1 text-xs focus:border-amber outline-none bg-white" data-id="element-435" />
                                    {rowIdx < displayRows.length - 1 && <button onClick={() => {
                            saveCampaignEdit();
                            handlePushRight(rowIdx);
                          }} className="w-4 h-4 flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0" title="Push this value to all months to the right" data-id="element-436">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-437">
                                          <polyline points="9 18 15 12 9 6" data-id="element-438" />
                                        </svg>
                                      </button>}
                                  </div>;
                      } else {
                        displayValue = <div className="flex justify-center items-center gap-0.5" data-id="element-439">
                                    <span onClick={() => startEditingCampaign(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-440">
                                      {formatted}
                                    </span>
                                    {rowIdx < displayRows.length - 1 ? <button onClick={() => handlePushRight(rowIdx)} className="w-4 h-4 flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0 opacity-0 group-hover:opacity-100" title="Push this value to all months to the right" data-id="element-441">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-442">
                                          <polyline points="9 18 15 12 9 6" data-id="element-443" />
                                        </svg>
                                      </button> : <div className="w-4 h-4 flex-shrink-0" data-id="element-444"></div>}
                                  </div>;
                      }
                    } else if (metric.key === 'buildFee' && metric.clickToEdit) {
                      // Click-to-edit build fee
                      if (editingBuildFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editBuildFeeValue} onChange={e => setEditBuildFeeValue(e.target.value)} onKeyDown={handleBuildFeeKeyDown} onBlur={saveBuildFeeEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs focus:border-amber outline-none bg-white" data-id="element-445" />;
                      } else {
                        displayValue = <span onClick={() => startEditingBuildFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-446">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'platformFee' && metric.clickToEdit) {
                      // Click-to-edit platform fee
                      if (editingPlatformFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editPlatformFeeValue} onChange={e => setEditPlatformFeeValue(e.target.value)} onKeyDown={handlePlatformFeeKeyDown} onBlur={savePlatformFeeEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs focus:border-amber outline-none bg-white" data-id="element-447" />;
                      } else {
                        displayValue = <span onClick={() => startEditingPlatformFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-448">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'campaignFee' && metric.clickToEdit) {
                      // Click-to-edit campaign fee
                      if (editingCampaignFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editCampaignFeeValue} onChange={e => setEditCampaignFeeValue(e.target.value)} onKeyDown={handleCampaignFeeKeyDown} onBlur={saveCampaignFeeEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs focus:border-amber outline-none bg-white" data-id="element-449" />;
                      } else {
                        displayValue = <span onClick={() => startEditingCampaignFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-450">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && metric.clickToEdit && rowIdx > 0) {
                      // Click-to-edit ad spend
                      if (editingAdSpend === rowIdx) {
                        displayValue = <div className="flex justify-center items-center gap-0.5" data-id="element-451">
                                    <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs focus:border-amber outline-none bg-white" data-id="element-452" />
                                    {rowIdx < displayRows.length - 1 && <button onClick={() => {
                            saveAdSpendEdit();
                            handlePushAdSpendRight(rowIdx);
                          }} className="w-4 h-4 flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0" title="Push this value to all months to the right" data-id="element-453">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-454">
                                          <polyline points="9 18 15 12 9 6" data-id="element-455" />
                                        </svg>
                                      </button>}
                                  </div>;
                      } else {
                        displayValue = <div className="flex justify-center items-center gap-0.5" data-id="element-456">
                                    <span onClick={() => startEditingAdSpend(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-457">
                                      {formatted}
                                    </span>
                                    {rowIdx < displayRows.length - 1 ? <button onClick={() => handlePushAdSpendRight(rowIdx)} className="w-4 h-4 flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0 opacity-0 group-hover:opacity-100" title="Push this value to all months to the right" data-id="element-458">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-459">
                                          <polyline points="9 18 15 12 9 6" data-id="element-460" />
                                        </svg>
                                      </button> : <div className="w-4 h-4 flex-shrink-0" data-id="element-461"></div>}
                                  </div>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx === 0) {
                      // Build column for Ad Spend - add invisible spacer for alignment
                      displayValue = <div className="flex justify-center items-center gap-0.5" data-id="element-462">
                                  <span data-id="element-463">{formatted}</span>
                                  <div className="w-4 h-4 flex-shrink-0" data-id="element-464"></div>
                                </div>;
                    } else {
                      displayValue = formatted;
                    }
                    // Special centering for Team Size
                    if (metric.centered) {
                      // It's already text-center by default in cellClass
                    }
                    return <td key={row.period} className={cellClass} data-id="element-465">
                                {(metric.key === 'activeCampaigns' || metric.key === 'adSpendPerCampaign' || metric.key === 'buildFee' || metric.key === 'platformFee' || metric.key === 'campaignFee') && (metric.clickToEdit || metric.editable) ?
                      // For editable cells, render directly without wrapper (they have their own centering)
                      <>{displayValue}</> :
                      // For non-editable cells, wrap in flex container
                      <div className="flex items-center justify-center gap-1" data-id="element-466">
                                    <span data-id="element-467">{displayValue}</span>
                                    {metric.key === 'teamSize' && <button className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center hover:bg-amber hover:text-navy flex-shrink-0" onMouseEnter={e => showTooltip(getFormula(metric.key, row, rowIdx), e)} onMouseLeave={hideTooltip} onClick={e => {
                          e.stopPropagation();
                          if (tooltip) hideTooltip();else showTooltip(getFormula(metric.key, row, rowIdx), e);
                        }} data-id="element-468">
                                        ?
                                      </button>}
                                    {metric.key !== 'teamSize' && !metric.editable && !metric.clickToEdit && <button className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center hover:bg-amber hover:text-navy flex-shrink-0" onMouseEnter={e => showTooltip(getFormula(metric.key, row, rowIdx), e)} onMouseLeave={hideTooltip} onClick={e => {
                          e.stopPropagation();
                          if (tooltip) hideTooltip();else showTooltip(getFormula(metric.key, row, rowIdx), e);
                        }} data-id="element-469">
                                          ?
                                        </button>}
                                  </div>}
                              </td>;
                  })}
                        </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {/* Tooltip */}
      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border-2 border-amber max-w-sm text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-470">
          {tooltip.content}
        </div>}
    </div>;
}